import Autocomplete from "@mui/material/Autocomplete";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import MUIStyledTextField from "components/MUIStyledComponents/MUIStyledTextField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appendUser, getUsers } from "store/slices/filtersSlice";
import StyledMuiCard from "utils/helpers/StyledMuiCard";
import useDebounce from "utils/hooks/useDebounceValue";

function UserInformation({ formik }) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.filters.users ?? []);
  const loading = useSelector((state) => state.filters?.loading?.users);
  const product = useSelector((state) => state.products.product);
  const [searchValue, setSearchValue] = useState("");
  const [option, setOption] = useState(null);
  const deferredValue = useDebounce(searchValue, 700);

  useEffect(() => {
    const data = {};
    if (deferredValue) {
      data.role = "user";
      data.fullname = deferredValue;
    }
    const id = setTimeout(() => {
      dispatch(getUsers(data));
    }, 500);
    return () => clearTimeout(id);
  }, [deferredValue, dispatch]);

  useEffect(() => {
    if (users?.length && formik.values.ownerId) {
      const user = users.find((user) => user?.id === formik.values.ownerId);
      setOption(user);
    } else if (users?.length) {
      setOption(null);
    }
  }, [formik.values.ownerId, users]);

  useEffect(() => {
    if (
      formik.values.ownerId &&
      !users.find((user) => user?.id === formik.values.ownerId)
    ) {
      dispatch(appendUser(product?.ownerId));
    }
  }, [dispatch, formik.values.ownerId, product?.ownerId, users]);

  return (
    <StyledMuiCard title="User Information" subTitle="Enter the user name">
      <CardContent>
        <Stack direction="column" gap={2} px={2}>
          <Autocomplete
            options={users}
            value={option}
            loading={loading}
            onChange={(_, option) => {
              formik.setFieldValue("ownerId", option?.id ?? "");
            }}
            inputValue={searchValue}
            onInputChange={(_, value) => {
              setSearchValue(value);
            }}
            onBlur={formik.handleBlur}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => option?.fullname}
            renderInput={(params) => (
              <MUIStyledTextField
                fullWidth
                {...params}
                label="User Name"
                name="ownerId"
                error={Boolean(formik.touched.ownerId && formik.errors.ownerId)}
                helperText={
                  formik.touched.ownerId && !!formik.errors.ownerId
                    ? formik.errors.ownerId
                    : "Please enter the user name"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.fullname}
              </li>
            )}
          />
        </Stack>
      </CardContent>
    </StyledMuiCard>
  );
}

UserInformation.propTypes = {
  formik: PropTypes.object.isRequired,
};

export default memo(UserInformation, arePropsEqual);

function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.formik.values.ownerId === nextProps.formik.values.ownerId &&
    prevProps.formik.touched.ownerId === nextProps.formik.touched.ownerId &&
    prevProps.formik.errors.ownerId === nextProps.formik.errors.ownerId
  );
}
